/**
 * The parts of onboarding by invitation (#780) that do not need React.
 *
 * These live here because they are rules rather than rendering, and each is the kind of thing that
 * regresses invisibly: where the token is allowed to appear, what the panel may do with a bad
 * response, and what a refused form is allowed to say. The screens above import them; nothing here
 * imports a screen.
 *
 * ## How the token travels through a URL
 *
 * `invitationUrl` writes it and `tokenFromHash` reads it back, so they are inverses and a change
 * to one that breaks the other is a broken link for a real customer. That pairing is the thing
 * worth testing, which is why they sit beside each other.
 *
 * **The token goes in the fragment, and nowhere else.** Browsers do not send fragments, so it stays
 * out of the server's access log, out of every proxy in between, and out of the `Referer` header of
 * whatever the page loads next. A token in the path or the query string is written to disk on every
 * hop, which for a credential that grants a tenant-admin account is not a small difference.
 *
 * ## What the panel does with the list it is given
 *
 * `invitationRows` exists because the handover panel renders inside the business Access section,
 * beside pause/resume — controls an owner may need urgently. A response of an unexpected shape
 * would otherwise throw inside `rows.find(...)` during render and take that whole section down
 * with it. A panel that cannot show its own list is a small problem; a panel that removes the
 * pause button is not.
 *
 * ## What a refusal is allowed to say
 *
 * #780 requires every refusal the link can produce to be German, and the API speaks English
 * throughout. So the screen never renders a server message: it maps the server's reason code to a
 * string of ours, and anything unrecognised becomes generic German. That last part is what makes
 * the rule hold for refusals nobody has written yet — a new server exception can be unhelpful to a
 * customer, but it cannot be English.
 */

/** Where the invited person lands. Built whole, so an owner copies a working URL. */
export function invitationUrl(token: string, origin: string, base: string): string {
  const root = `${origin}${base.replace(/\/+$/, "")}`;
  return `${root}/einladung#${encodeURIComponent(token)}`;
}

/**
 * Where an invited colleague lands (#1298).
 *
 * A separate path rather than a second meaning for `/einladung`, because the two token spaces are
 * separate tables: a single screen would have to try one then the other, and the failed first try
 * would make each kind of link a probe for the other. Same fragment rule, for the same reason.
 */
export function staffInvitationUrl(token: string, origin: string, base: string): string {
  const root = `${origin}${base.replace(/\/+$/, "")}`;
  return `${root}/team-einladung#${encodeURIComponent(token)}`;
}

/** Reads the token back out of `#…`, tolerating a leading `#` and percent-encoding. */
export function tokenFromHash(hash: string): string {
  const raw = hash.startsWith("#") ? hash.slice(1) : hash;
  if (!raw) return "";
  try {
    return decodeURIComponent(raw);
  } catch {
    // A malformed escape is not a reason to lose the token: hand back what was there and let the
    // server be the one that decides it is not a valid invitation.
    return raw;
  }
}

/** German wording for each refusal the server can name, keyed by its `AcceptRefusal` code. */
const REFUSAL_KEYS: Record<string, string> = {
  invalid_email: "invite.accept.refusal.invalidEmail",
  invalid_slug: "invite.accept.refusal.invalidSlug",
  too_long: "invite.accept.refusal.tooLong",
  email_unavailable: "invite.accept.refusal.emailUnavailable",
  slug_taken: "invite.accept.refusal.slugTaken",
  already_used: "invite.accept.refusal.alreadyUsed",
  // #1298: the business is at its seat limit. Not the colleague's problem to solve, so the
  // wording points them back at whoever invited them rather than at a form field.
  seat_cap: "invite.accept.refusal.seatCap",
};

/**
 * Which German string to show for a failed submission — a translation key, never a message.
 *
 * The reason is read from the parsed response body only. An error whose *text* happens to contain
 * a code word must not be able to select wording: the server says what happened, not its prose.
 */
/**
 * Was this the rate limit rather than an answer? (#1163)
 *
 * A 429 says nothing about the link: it is valid, the caller's budget for the next few minutes is
 * spent. Every screen that asks the onboarding routes has to tell the two apart, because
 * rendering it as "link not usable" sends a genuinely invited owner away from a working link.
 */
export function isThrottled(error: unknown): boolean {
  return (error as { status?: unknown })?.status === 429;
}

/**
 * Minutes to wait, from the `Retry-After` the server sends with a 429 (#1163), rounded up and at
 * least one; ten when the header is missing, which is the window the server uses.
 */
export function retryAfterMinutes(error: unknown): number {
  const raw = (error as { retryAfterSeconds?: unknown })?.retryAfterSeconds;
  if (typeof raw !== "number" || !Number.isFinite(raw) || raw <= 0) return 10;
  return Math.max(1, Math.ceil(raw / 60));
}

export function refusalKey(error: unknown): string {
  if (isThrottled(error)) return "invite.accept.refusal.throttled";
  const reason = (error as { data?: { reason?: unknown } })?.data?.reason;
  return (typeof reason === "string" && REFUSAL_KEYS[reason]) || "invite.accept.refusal.generic";
}

/**
 * Whatever the invitations endpoint returned, as something safe to render.
 *
 * Non-arrays become empty rather than throwing. The panel then shows "no link created yet", which
 * is wrong but harmless and self-correcting on reload — unlike a render-time exception, which is
 * neither.
 */
export function invitationRows<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : [];
}
